import { useSelector } from "@xstate/store-react";
import { useMemo, useState } from "react";
import { createQuestionnaireBuilderStore } from "./questionnaire-builder-store";
import type { BuilderItemType, DraftAnswerOption, DraftEnableWhen, DraftNode, QuestionnaireDraft } from "./types";
import { getItemAtPath } from "./utils/path-helpers";

export function useQuestionnaireBuilder(initial?: Partial<QuestionnaireDraft>, scoringRequired = false) {
  const [store] = useState(() => createQuestionnaireBuilderStore(initial, scoringRequired));

  const draft = useSelector(store, (s) => s.context.draft);
  const selectedItemPath = useSelector(store, (s) => s.context.selectedItemPath);
  const scoringEnabled = useSelector(store, (s) => s.context.scoringEnabled);
  const isDirty = useSelector(store, (s) => s.context.isDirty);

  const selectedItem = useMemo(() => {
    if (!selectedItemPath) return null;
    return getItemAtPath(draft.sections, selectedItemPath) ?? null;
  }, [draft.sections, selectedItemPath]);

  const actions = useMemo(
    () => ({
      addItem: (parentPath: number[], itemType: BuilderItemType) =>
        store.send({ type: "item.add", parentPath, itemType }),
      removeItem: (path: number[]) => store.send({ type: "item.remove", path }),
      moveItem: (fromPath: number[], toIndex: number) => store.send({ type: "item.move", fromPath, toIndex }),
      updateItem: (path: number[], updates: Partial<DraftNode>) => store.send({ type: "item.update", path, updates }),
      selectItem: (path: number[] | null) => store.send({ type: "item.select", path }),
      duplicateItem: (path: number[]) => store.send({ type: "item.duplicate", path }),
      addOption: (path: number[]) => store.send({ type: "option.add", path }),
      removeOption: (path: number[], index: number) => store.send({ type: "option.remove", path, index }),
      updateOption: (path: number[], index: number, option: DraftAnswerOption) =>
        store.send({ type: "option.update", path, index, option }),
      addEnableWhen: (path: number[]) => store.send({ type: "enableWhen.add", path }),
      removeEnableWhen: (path: number[], index: number) => store.send({ type: "enableWhen.remove", path, index }),
      updateEnableWhen: (path: number[], index: number, condition: DraftEnableWhen) =>
        store.send({ type: "enableWhen.update", path, index, condition }),
      setScoringEnabled: (enabled: boolean) => store.send({ type: "scoring.set", enabled }),
      setScores: (scores: QuestionnaireDraft["scores"]) => store.send({ type: "scores.set", scores }),
      setTotalBands: (bands: NonNullable<QuestionnaireDraft["totalBands"]>) =>
        store.send({ type: "totalBands.set", bands }),
      markSaved: () => store.send({ type: "questionnaire.saved" }),
    }),
    [store],
  );

  return { draft, scoringEnabled, selectedItemPath, selectedItem, isDirty, actions, store };
}

export type QuestionnaireBuilderActions = ReturnType<typeof useQuestionnaireBuilder>["actions"];
